import clsx from 'clsx';
import { useState } from 'react';

import { routes } from '@/assets/constants/routes-no.ts';
import { CompetitionWinnersDialog } from '@/components/CompetitionWinnersDialog/index.ts';
import { LoanExampleText } from '@/components/LoanExampleText/index.ts';
import { UnderlinedText } from '@/components/UnderlinedText/index.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import type { CompetitionWinner, CompetitionWithConfig } from '@/lib/api/requests.ts';
import { NumberCircle } from '@/ui/NumberCircle.tsx';
import { NumberedList, NumberedListItem } from '@/ui/NumberedList.tsx';

import Clouds from '../_assets/clouds.svg?react';
import Mountain from '../_assets/mountain.svg?react';
import Ski from '../_assets/ski.svg?react';
import Snow from '../_assets/snow.svg?react';
import { CTA } from '../_components/CTA.tsx';
import { useHasEnded } from '../_components/useHasEnded.ts';

export const TopSection = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
  allWinners: CompetitionWinner[];
}) => {
  return (
    <>
      <div
        className="relative isolate flex w-full flex-col overflow-hidden text-[#223C66]"
        style={{
          background: 'linear-gradient(180deg, #D1ECFE 0%, #E8F6FF 100%)',
        }}
      >
        <div className="mx-auto w-full max-w-[870px] p-6 md:px-0">
          <Content
            activeCompetition={props.activeCompetition}
            winners={props.winners}
            allWinners={props.allWinners}
          />
        </div>

        <Background />
      </div>

      <Disclaimer />
    </>
  );
};

const Disclaimer = () => (
  <div className="bg-white pt-4">
    <div className="container mx-auto px-5 text-center typography-v1-body4 text-balance">
      <LoanExampleText />
    </div>
  </div>
);

const Card = (props: React.ComponentProps<'div'>) => (
  <div
    {...props}
    className={clsx('rounded-2xl bg-[#FBFDFF] p-4 sm:p-8', props.className)}
    style={{
      boxShadow: '2px 4px 40px 10px rgba(199, 205, 209, 0.20)',
      ...props.style,
    }}
  />
);

const Content = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
  allWinners: CompetitionWinner[];
}) => {
  const ended = useHasEnded();
  const [isOpen, setIsOpen] = useState(false);
  const [isAllOpen, setIsAllOpen] = useState(false);

  return (
    <div className="flex flex-col pt-4 pb-64 sm:pb-80 md:pt-8 md:pb-120 xl:pb-20">
      <h1 className="mb-4 text-center text-4xl leading-[1.2] font-bold text-[#18A0FB] sm:text-5xl md:mb-10 xl:text-[4.3rem]">
        Er det du som blir <br />
        <UnderlinedText variant="2" distance="0.2em">
          gjeldsfri
        </UnderlinedText>{' '}
        denne påsken?
      </h1>

      <Card className="mb-4 typography-v1-body1 text-balance">
        Vi trekker en heldig vinner som får slettet inntil{' '}
        <span className="font-bold text-[#18A0FB]">500&nbsp;000&nbsp;kr</span> av sin forbruks-,
        kredittkort- og boliglånsgjeld.
      </Card>

      <Card className="mb-4 flex items-center gap-2 md:gap-4">
        <svg
          className="shrink-0"
          xmlns="http://www.w3.org/2000/svg"
          width="27"
          height="27"
          fill="none"
          viewBox="0 0 27 27"
        >
          <g stroke="#18a0fb" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.25">
            <path d="M13.28 23.24a9.96 9.96 0 1 0 0-19.92 9.96 9.96 0 0 0 0 19.92M13.28 8.85h.01" />
            <path d="M12.18 13.28h1.1v4.43h1.11" />
          </g>
        </svg>
        <span className="typography-v1-body3">
          For å delta i trekningen må du søke om lavere rente på din forbruks- og kredittkortgjeld.
        </span>
      </Card>

      <div className="flex flex-col gap-4 md:flex-row">
        <Card className="flex-2">
          <h3 className="mb-2 typography-v1-h3 md:mb-6">For å delta:</h3>
          <div className="mb-4 flex flex-col">
            <NumberedList
              renderNumber={(props) => (
                <NumberCircle
                  sx={{
                    fontSize: 20,
                    height: 32,
                    backgroundColor: '#FDD183',
                    color: '#354661',
                    fontWeight: 700,
                  }}
                  {...props}
                />
              )}
              renderLabel={(props) => <div {...props} className="ml-1 typography-v1-body3" />}
            >
              <NumberedListItem>Logg inn med bank ID</NumberedListItem>
              <NumberedListItem>Sjekk om du kan få lavere rente</NumberedListItem>
              <NumberedListItem>Delta i trekningen</NumberedListItem>
            </NumberedList>
          </div>

          <a
            href={routes.MARCH_2026_COMPETITION_TERMS}
            className="mb-6 block typography-v1-body4 underline"
          >
            Vilkår og betingelser for deltagelse
          </a>

          {ended && (
            <p className="-mt-4 mb-4 typography-v1-h4">
              <span>Denne trekningen er avsluttet. </span>
              <button
                onClick={() => setIsOpen(true)}
                className="mb-0.5 ml-1 font-semibold text-[#18A0FB] underline"
              >
                Se vinneren her
              </button>
            </p>
          )}

          <CTA
            activeCompetition={props.activeCompetition}
            onClick={() => {
              sendUserPortalEvent('mar26_competition_cta_top_clicked');
            }}
          />
        </Card>

        <Card className="flex flex-3 flex-col gap-4">
          <h3 className="typography-v1-body3">Tidligere vinnere:</h3>

          {props.allWinners.slice(0, 3).map((winner, index) => (
            <div
              key={winner.name + winner.age + winner.city}
              className="flex items-center gap-2 rounded-lg bg-[#F0F4FA] px-4 py-3 typography-v1-body4"
            >
              <span className="flex size-6 items-center justify-center rounded-full bg-[#031839] text-xs font-bold text-white">
                {props.allWinners.length - index}
              </span>
              <span className="flex-2">{winner.name}</span>
              <span className="text-nowrap">{winner.age}</span>
              <span className="flex-1 text-right">{winner.city}</span>
            </div>
          ))}

          <button
            onClick={() => setIsAllOpen(true)}
            className="mr-auto typography-v1-body4 text-[#18A0FB]"
          >
            Se alle
          </button>
        </Card>
      </div>

      {props.winners && (
        <CompetitionWinnersDialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          winners={props.winners}
        />
      )}

      <CompetitionWinnersDialog
        open={isAllOpen}
        onClose={() => setIsAllOpen(false)}
        winners={props.allWinners}
      />
    </div>
  );
};

const Background = () => (
  <div className="-z-10">
    <Mountain className="absolute bottom-10 left-0 origin-bottom-left scale-40 sm:bottom-4 sm:scale-75 md:-bottom-4 md:scale-100" />
    <Snow preserveAspectRatio="none" className="absolute right-0 bottom-0 left-0 w-full" />
    <Ski className="absolute -right-6 bottom-0 origin-bottom-right scale-40 sm:scale-50 md:scale-75 xl:scale-100 2xl:right-10" />
    <Clouds className="absolute top-1/2 hidden xl:block" />
  </div>
);
